import { Link } from "react-router-dom";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_narcan-dispenser-pro/artifacts/avzlwoog_IMG_9826.JPG";

export const Footer = () => {
    return (
        <footer
            data-testid="footer"
            className="bg-[#0A1428] text-slate-400 pt-24 pb-10 relative overflow-hidden"
        >
            <div className="absolute inset-0 dot-grid-light opacity-20 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-[480px] h-[480px] opacity-[0.05] pointer-events-none">
                <svg viewBox="0 0 200 200">
                    <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                </svg>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                {/* Big CTA strip */}
                <div className="border-y border-white/10 py-12 md:py-16 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                            Ready to deploy
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tighter text-white text-balance max-w-2xl">
                            Close the access
                            <br />
                            gap in your community.
                        </h3>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="footer-cta"
                        className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end"
                    >
                        Start a conversation
                    </Link>
                </div>

                {/* Columns */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt="Kairos ORD"
                                className="h-10 w-10 object-contain rounded-sm bg-white"
                            />
                            <div className="flex flex-col leading-none">
                                <span className="font-display font-extrabold tracking-tight text-white text-lg">
                                    KAIROS<span className="text-[#F0493E]">+</span>
                                </span>
                                <span className="font-body text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">
                                    ORD LLC
                                </span>
                            </div>
                        </div>
                        <p className="mt-6 text-sm md:text-[15px] text-slate-400 max-w-sm leading-relaxed font-medium">
                            The Kairos Overdose Response Device — a rugged,
                            connected Naloxone dispenser built for the
                            communities, facilities, and systems closing the
                            access gap for OTC emergency supplies.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#F0493E]" />
                            Built in the United States
                        </div>
                    </div>

                    <FooterCol
                        title="Public Safety"
                        items={[
                            { to: "/public-safety/overdose-response", label: "Overdose Response" },
                            { to: "/public-safety/drink-spike-detection", label: "Drink Spike Detection" },
                        ]}
                    />
                    <FooterCol
                        title="Alert & Automation"
                        items={[
                            { to: "/alert-automation#ln2-scale", label: "LN₂ Scale + Workflow" },
                            { to: "/alert-automation#equipment-alerts", label: "Equipment Alerts" },
                            { to: "/alert-automation#automated-weight-alerts", label: "Automated Weight Alerts" },
                            { to: "/alert-automation#custom-alert-systems", label: "Custom Alert Systems" },
                        ]}
                    />
                    <FooterCol
                        title="Company"
                        items={[
                            { to: "/products", label: "Products" },
                            { to: "/auto-shops", label: "For Auto Shops" },
                            { to: "/about", label: "About" },
                            { to: "/pilot", label: "Pilot Program" },
                            { to: "/support", label: "Support" },
                            { to: "/contact", label: "Contact" },
                            { to: "/privacy", label: "Privacy Policy" },
                        ]}
                    />

                    <div className="md:col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                            Direct
                        </div>
                        <a
                            href="mailto:kairosord@gmail.com"
                            data-testid="footer-email"
                            className="mt-3 link-underline block text-white text-sm font-semibold break-all"
                        >
                            kairosord@gmail.com
                        </a>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-xs text-slate-500 font-medium">
                        © {new Date().getFullYear()} Kairos ORD LLC. All rights reserved. Built for the moment that matters.
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                        <span>Overdose Response Device</span>
                        <span className="text-[#F0493E]">·</span>
                        <span>Naloxone Dispenser</span>
                        <span className="text-[#F0493E]">·</span>
                        <span>OTC Emergency Supplies</span>
                        <span className="text-[#F0493E]">·</span>
                        <span>Community Wellness Infrastructure</span>
                    </div>
                </div>

                {/* Regulatory / compliance disclaimer */}
                <div
                    data-testid="footer-disclaimer"
                    className="mt-6 pt-6 border-t border-white/5"
                >
                    <div className="flex items-start gap-3 text-[11px] leading-relaxed text-slate-500 font-medium max-w-4xl">
                        <div className="shrink-0 mt-0.5 h-4 w-4 grid place-items-center border border-slate-600 text-slate-500 text-[9px] font-bold tracking-wider">
                            i
                        </div>
                        <p>
                            <span className="text-slate-400 font-bold uppercase tracking-[0.22em] text-[10px] mr-2">
                                Regulatory Note ·
                            </span>
                            Kairos ORD units dispense over-the-counter
                            Naloxone in accordance with its FDA-approved OTC
                            label. The Kairos dispenser is a distribution and
                            access-control device — it is not itself a
                            medical device and does not diagnose, treat, cure,
                            or prevent any condition. Users should follow the
                            instructions on the Naloxone packaging and call
                            emergency services in the event of a suspected
                            overdose.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

const FooterCol = ({ title, items }) => (
    <div className="md:col-span-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            {title}
        </div>
        <ul className="mt-3 space-y-2.5">
            {items.map((item) => (
                <li key={item.to + item.label}>
                    <Link
                        to={item.to}
                        data-testid={`footer-link-${item.label
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                        className="link-underline text-sm text-slate-300 hover:text-white"
                    >
                        {item.label}
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);
